<template>
  <div class="sf-start">
    <!-- ----------------- -->
    <!-- NOT AUTHENTICATED -->
    <!-- ----------------- -->
    <v-card class="sf-card sf-start__signin"
            flat
            v-if="!authenticated">
      <v-card-text class="sf-card__body">
        <h2 class="text-h5 mb-2">{{ $t('signIn') }}</h2>
        <p class="sf-muted">{{ $t('pleaseEnterTheInvitationCodeYouWillFindItInYourOfficialInvitationEmail') }}</p>
        <v-text-field class="mb-4"
                      hide-details="auto"
                      outlined
                      v-bind:append-icon="this.error ? 'ti-alert-circle' : null"
                      v-bind:error="this.error"
                      v-bind:label="$t('invitationCode')"
                      v-model="invitationCode"
                      v-on:keyup="uppercase"
                      v-on:keyup.enter="signIn"
                      v-mask="isCustomerCode ?'NNNN-NNNN-NNNN-NNNN' : null" />
        <v-btn color="accent"
               depressed
               large
               v-bind:block="$vuetify.breakpoint.xsOnly"
               v-bind:disabled="invitationCode == null || invitationCode.length < 1"
               v-bind:loading="loading"
               v-on:click="signIn">
          <v-icon left>ti-login-2</v-icon>
          {{ $t('signIn') }}
        </v-btn>
        <v-btn class="ml-sm-2 mt-2 mt-sm-0"
               color="primary"
               text
               v-bind:block="$vuetify.breakpoint.xsOnly"
               v-on:click="$emit('forgot')">
          {{ $t('requestInvitationCode') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- ------------- -->
    <!-- AUTHENTICATED -->
    <!-- ------------- -->
    <template v-else>
      <v-card class="sf-card"
              flat>
        <div class="sf-card__header">
          <div>
            <h2 class="sf-card__title">{{ $t('welcome', { fullSalutation }) }}</h2>
            <p class="sf-card__subtitle"
               v-if="isGuest && hostName != null">{{ $t('signedInAsGuest', { id: currentCustomer.id, company: companyName, host: hostName }) }}</p>
            <p class="sf-card__subtitle"
               v-else>{{ $t('signedInAs', { id: currentCustomer.id, company: companyName }) }}</p>
          </div>
          <div class="sf-card__tools">
            <v-btn color="primary"
                   outlined
                   small
                   v-on:click="changeCustomer">
              <v-icon left
                      small>ti-switch-horizontal</v-icon>
              {{ $t('changeCustomer') }}
            </v-btn>
          </div>
        </div>

        <!-- NOT LIVE: Countdown -->
        <v-card-text class="sf-card__body"
                     v-if="!isLive">
          <div class="sf-start__countdown">
            <div>
              <div class="sf-section-title">{{ $t('startsIn') }}</div>
              <countdown v-bind:date="event.startDate" />
            </div>
            <v-chip class="sf-pill sf-pill--primary"
                    small>
              <v-icon left
                      small>ti-calendar</v-icon>
              {{ event.startDate | formatDate('LLL') }}
            </v-chip>
          </div>
          <!--<div class="mt-4">
            <p>Am <strong>{{ event.startDate | formatDate('LLL') }}</strong> finden Sie unter dieser Adresse unsere Bestell-Web-App, mit deren Hilfe Sie sowohl vom PC, vom Tablet als auch von Ihrem Smartphone aus Ihre Bestellmengen komfortabel erfassen, korrigieren und später ausdrucken können. Wir freuen uns auf Ihre Teilnahme und hoffen, Ihnen so einen komfortablen und einfachen Bestellvorgang zu ermöglichen!</p>
          </div>-->
          <!--<p>Unsere Hausmesse ist beendet. Vielen Dank für Ihre Teilnahme.</p>-->
        </v-card-text>
      </v-card>

      <!-- LIVE: Aktionskacheln -->
      <section class="sf-start__section"
               v-if="isLive">
        <h3 class="sf-section-title">{{ $t('getStarted') }}</h3>
        <div class="sf-start__tiles">
          <v-card class="sf-card sf-card--interactive sf-tile sf-tile--accent"
                  flat
                  v-bind:to="{ name: 'new-products' }">
            <div class="sf-tile__icon">
              <v-icon>ti-star</v-icon>
            </div>
            <div class="sf-tile__title">{{ $t('discoverNewProducts') }}</div>
            <div class="sf-tile__text">{{ $t('discoverNewProductsText') }}</div>
            <div class="sf-tile__cta">{{ $t('open') }} <v-icon small>ti-arrow-right</v-icon></div>
          </v-card>
          <v-card class="sf-card sf-card--interactive sf-tile"
                  flat
                  v-bind:to="{ name: 'suppliers' }">
            <div class="sf-tile__icon">
              <v-icon>ti-glass-full</v-icon>
            </div>
            <div class="sf-tile__title">{{ $t('ourSuppliers') }}</div>
            <div class="sf-tile__text">{{ $t('ourSuppliersText') }}</div>
            <div class="sf-tile__cta">{{ $t('open') }} <v-icon small>ti-arrow-right</v-icon></div>
          </v-card>
          <v-card class="sf-card sf-card--interactive sf-tile"
                  flat
                  v-bind:to="{ name: 'floor-plan' }">
            <div class="sf-tile__icon">
              <v-icon>ti-map-2</v-icon>
            </div>
            <div class="sf-tile__title">{{ $t('floorPlan') }}</div>
            <div class="sf-tile__text">{{ $t('floorPlanText') }}</div>
            <div class="sf-tile__cta">{{ $t('open') }} <v-icon small>ti-arrow-right</v-icon></div>
          </v-card>
          <v-card class="sf-card sf-card--interactive sf-tile"
                  flat
                  href="https://portal.sagasser.de/booking/public/categories/Hausmesse/Allgemein"
                  target="_blank">
            <div class="sf-tile__icon">
              <v-icon>ti-calendar</v-icon>
            </div>
            <div class="sf-tile__title">{{ $t('ourEvents') }}</div>
            <div class="sf-tile__text">{{ $t('ourEventsText') }}</div>
            <div class="sf-tile__cta">{{ $t('open') }} <v-icon small>ti-external-link</v-icon></div>
          </v-card>
        </div>
      </section>

      <!-- BOTH CASES: Vorabinformationen – vom Administrator gepflegt (Administration → Vorabinformationen) -->
      <section class="sf-start__section">
        <h3 class="sf-section-title">{{ $t('preliminaryInformation') }}</h3>
        <div class="sf-start__tiles sf-start__tiles--docs"
             v-if="publishedInfoDocuments.length > 0">
          <v-card class="sf-card sf-card--interactive sf-doc"
                  flat
                  rel="noopener"
                  target="_blank"
                  v-bind:href="document.url"
                  v-bind:key="document.id"
                  v-for="document in publishedInfoDocuments">
            <v-icon class="sf-doc__icon">ti-file-type-pdf</v-icon>
            <div class="sf-doc__body">
              <div class="sf-doc__title">{{ document.title }}</div>
              <div class="sf-doc__meta">{{ documentMeta(document) }}</div>
            </div>
          </v-card>
        </div>
        <p class="sf-muted mb-0"
           v-else>{{ $t('preliminaryInformationComingSoon') }}</p>
      </section>
    </template>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  import Countdown from '@/components/common/Countdown'

  export default {
    components: {
      Countdown,
    },
    computed: {
      isCustomerCode() {
        return this.invitationCode != null && this.invitationCode.startsWith('CUS')
      },
      authenticated() {
        return this.currentCustomer != null
      },
      companyName() {
        if (this.currentCustomer == null) {
          return null
        }

        return this.currentCustomer.companyName
      },
      // Angemeldet mit einem Gastcode (participantSlot 1–10): die Person hinter dem Code ist ein Gast des Kunden.
      isGuest() {
        return this.currentCustomer != null && this.currentCustomer.participantSlot > 0
      },
      hostName() {
        if (this.currentCustomer == null) {
          return null
        }

        const name = `${this.currentCustomer.givenName || ''} ${this.currentCustomer.familyName || ''}`.trim()

        return name.length === 0 ? null : name
      },
      fullSalutation() {
        if (this.currentCustomer == null) {
          return null
        }

        if (this.isGuest) {
          const guestName = `${this.currentCustomer.participantGivenName || ''} ${this.currentCustomer.participantFamilyName || ''}`.trim()

          if (guestName.length === 0) {
            return `${this.currentCustomer.id} ${this.currentCustomer.companyName}`
          }

          return `${guestName} (${this.currentCustomer.id} ${this.currentCustomer.companyName})`
        }

        if (this.currentCustomer.salutation == null || this.currentCustomer.givenName == null || this.currentCustomer.familyName == null) {
          return `${this.currentCustomer.id} ${this.currentCustomer.companyName}`
        }

        const salutation = this.currentCustomer.salutation.trim()

        if (salutation === 'Herr') {
          return `Herr ${this.currentCustomer.familyName} (${this.currentCustomer.id} ${this.currentCustomer.companyName})`
        } else if (salutation === 'Frau') {
          return `Frau ${this.currentCustomer.familyName} (${this.currentCustomer.id} ${this.currentCustomer.companyName})`
        } else if (salutation === 'Familie') {
          return `Familie ${this.currentCustomer.familyName} (${this.currentCustomer.id} ${this.currentCustomer.companyName})`
        } else {
          return `${this.currentCustomer.givenName} ${this.currentCustomer.familyName} (${this.currentCustomer.id} ${this.currentCustomer.companyName})`
        }
      },
      ...mapGetters([
        'currentCustomer',
        'isLive',
        'publishedInfoDocuments'
      ])
    },
    async created() {
      try {
        await this.$store.dispatch('loadInfoDocuments')
      } catch (e) {
        // Ohne Liste bleibt der Hinweis „in Kürze“ stehen – die Startseite soll daran nicht scheitern.
        console.warn('Vorabinformationen konnten nicht geladen werden', e)
      }
    },
    data: () => ({
      invitationCode: null
    }),
    methods: {
      documentMeta(document) {
        if (document.description != null && document.description.length > 0) {
          return document.description
        }

        return `PDF · ${this.$options.filters.formatFileSize(document.size)}`
      },
      uppercase() {
        if (this.invitationCode == null) {
          return
        }

        this.invitationCode = this.invitationCode.toUpperCase()
      },
      async changeCustomer() {
        this.$emit('signOutCustomer')
      },
      async signIn() {
        this.$emit('signInCustomer', this.invitationCode)
      }
    },
    name: 'StartingSoonCard',
    props: {
      event: Object,
      error: Boolean,
      loading: Boolean
    }
  }
</script>

<style scoped>
  .sf-start {
    margin-top: 24px;
  }

  .sf-start__signin {
    max-width: 560px;
  }

  .sf-start__section {
    margin-top: 32px;
  }

  .sf-start__countdown {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 16px 32px;
    justify-content: space-between;
  }

  .sf-start__tiles {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }

  .sf-start__tiles--docs {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }

  .sf-tile {
    display: flex;
    flex-direction: column;
    padding: 20px 24px;
  }

  .sf-tile__icon {
    align-items: center;
    background: var(--sf-primary-soft);
    border-radius: 12px;
    color: var(--sf-primary);
    display: inline-flex;
    height: 44px;
    justify-content: center;
    margin-bottom: 16px;
    width: 44px;
  }

  .sf-tile__icon .v-icon {
    color: inherit;
  }

  .sf-tile--accent .sf-tile__icon {
    background: var(--sf-accent-soft);
    color: var(--sf-accent);
  }

  .sf-tile__title {
    color: var(--sf-text);
    font-size: 1.0625rem;
    font-weight: 600;
    letter-spacing: -.01em;
    margin-bottom: 4px;
  }

  .sf-tile__text {
    color: var(--sf-text-secondary);
    font-size: .875rem;
    line-height: 1.5;
  }

  .sf-tile__cta {
    align-items: center;
    color: var(--sf-primary);
    display: inline-flex;
    font-size: .875rem;
    font-weight: 600;
    gap: 4px;
    margin-top: auto;
    padding-top: 16px;
  }

  .sf-tile__cta .v-icon {
    color: inherit;
  }

  .sf-tile--accent .sf-tile__cta {
    color: var(--sf-accent);
  }

  .sf-doc {
    align-items: center;
    display: flex;
    gap: 14px;
    padding: 14px 18px;
  }

  .sf-doc__icon {
    color: var(--sf-accent);
    font-size: 32px;
  }

  .sf-doc__title {
    color: var(--sf-text);
    font-size: .9375rem;
    font-weight: 600;
    line-height: 1.35;
  }

  .sf-doc__meta {
    color: var(--sf-text-muted);
    font-size: .75rem;
    font-weight: 600;
    letter-spacing: .06em;
    margin-top: 2px;
  }

  @media (max-width: 599px) {
    .sf-start {
      margin-top: 16px;
    }

    .sf-start__section {
      margin-top: 24px;
    }
  }
</style>

<i18n>
  {
    "de": {
      "requestInvitationCode": "Einladungscode anfordern",
      "preliminaryInformation": "Vorabinformationen",
      "preliminaryInformationComingSoon": "Vorabinformationen werden in Kürze bereitgestellt.",
      "discoverNewProducts": "Neuigkeiten entdecken",
      "ourSuppliers": "Unsere Lieferanten",
      "floorPlan": "Lageplan",
      "floorPlanText": "Die Messehalle als interaktiver Plan – Lieferanten finden und direkt in den Ordersatz springen.",
      "changeCustomer": "Kunde wechseln",
      "invitationCode": "Einladungscode",
      "pleaseEnterTheInvitationCodeYouWillFindItInYourOfficialInvitationEmail": "Bitte geben Sie den Einladungscode ein. Diesen finden Sie in Ihrer offiziellen Einladungs-E-Mail.",
      "signIn": "Anmelden",
      "welcome": "Willkommen, {fullSalutation}!",
      "viewOrders": "Bestellungen ansehen",
      "ourEvents": "Unsere Veranstaltungen",
      "signedInAs": "Kundennummer {id} · {company}",
      "signedInAsGuest": "Kundennummer {id} · {company} · Gast von {host}",
      "startsIn": "Die Hausmesse startet in",
      "getStarted": "Jetzt loslegen",
      "open": "Öffnen",
      "discoverNewProductsText": "Alle neuen Artikel nach Lieferant sortiert – mit einem Klick in den Ordersatz.",
      "ourSuppliersText": "Alle Ordersätze alphabetisch – Bestellmengen erfassen, korrigieren und drucken.",
      "ourEventsText": "Veranstaltungen rund um die Hausmesse im Buchungsportal."
    },
    "en": {
      "requestInvitationCode": "Request invitation code",
      "preliminaryInformation": "Preliminary information",
      "preliminaryInformationComingSoon": "Preliminary information will be provided shortly.",
      "discoverNewProducts": "Discover new products",
      "ourSuppliers": "Our suppliers",
      "floorPlan": "Floor plan",
      "floorPlanText": "The exhibition hall as an interactive plan – find suppliers and jump straight to the order sheet.",
      "changeCustomer": "Change customer",
      "invitationCode": "Invitation code",
      "pleaseEnterTheInvitationCodeYouWillFindItInYourOfficialInvitationEmail": "Please enter the invitation code. You will find it in your official invitation email.",
      "signIn": "Sign in",
      "welcome": "Welcome, {fullSalutation}!",
      "viewOrders": "View orders",
      "ourEvents": "Our events",
      "signedInAs": "Customer number {id} · {company}",
      "signedInAsGuest": "Customer number {id} · {company} · guest of {host}",
      "startsIn": "The fair starts in",
      "getStarted": "Get started",
      "open": "Open",
      "discoverNewProductsText": "All new products sorted by supplier – one click to the order sheet.",
      "ourSuppliersText": "All order sheets alphabetically – enter, correct and print your quantities.",
      "ourEventsText": "Events around the fair in the booking portal."
    }
  }
</i18n>